// src/chat/lessonStore.ts
// Loads lesson JSON from /public via fetch().
// Minimal v0: supports CT module + returns intro bubbles + Chapter 1 board.

export type LessonId = "ct-maker";

export type LessonOption = {
  id: LessonId;
  label: string;
  description?: string;
};

// v0 catalog (scale later by reading a registry JSON from /public)
const LESSON_OPTIONS: LessonOption[] = [
  {
    id: "ct-maker",
    label: "Computational Thinking – Computer Maze",
    description: "Testles: Intro + Hoofdstuk 1 (Wat is Computational Thinking?)",
  },
];

export function listLessonOptions(): LessonOption[] {
  return LESSON_OPTIONS;
}

const STORAGE_KEY = "tt_active_lesson_v1";

export function setActiveLesson(id: LessonId | null) {
  if (!id) localStorage.removeItem(STORAGE_KEY);
  else localStorage.setItem(STORAGE_KEY, id);
}

export function getActiveLessonId(): LessonId | null {
  const raw = localStorage.getItem(STORAGE_KEY);
  return raw === "ct-maker" ? "ct-maker" : null;
}

export type LessonBlueprint = {
  id: LessonId;
  title: string;
  introBubbles: string[];
  basePath: string; // e.g. "/content/nl/computer-maze"
};

let cacheIndex: any | null = null;
let cacheChapter1: any | null = null;

async function fetchJson(path: string) {
  const res = await fetch(path, { cache: "no-store" });
  if (!res.ok) throw new Error(`Failed to load ${path}: ${res.status}`);
  return res.json();
}

export async function loadActiveLessonBlueprint(): Promise<LessonBlueprint | null> {
  const id = getActiveLessonId();
  if (!id) return null;

  // For now only CT
  const basePath = "/content/nl/computer-maze";

  if (!cacheIndex) {
    cacheIndex = await fetchJson(`${basePath}/index.json`);
  }

  const title = cacheIndex?.gameConfig?.title ?? "Denken als een Maker";
  const bubbles =
    cacheIndex?.gameConfig?.intro?.outcomes?.slice(0, 10) ??
    [
      "Ik kan een probleem opdelen in stappen.",
      "Ik kan patronen herkennen.",
      "Ik kan fouten vinden en verbeteren (debuggen).",
    ];

  return { id, title, introBubbles: bubbles, basePath };
}

export async function loadCTChapter1Board(lesson: LessonBlueprint) {
  if (!cacheChapter1) {
    cacheChapter1 = await fetchJson(`${lesson.basePath}/1-WatIsComputationalThinking.json`);
  }
  

  const firstStep = Array.isArray(cacheChapter1?.steps) ? cacheChapter1.steps[0] : null;
  const content = firstStep?.content;
  const stages = Array.isArray(content?.stages) ? content.stages : [];
  const bestStage = stages.length ? stages[stages.length - 1] : null;
  const blocks = Array.isArray(bestStage?.blocks) ? bestStage.blocks : [];

  const banner = blocks.find(
    (b: any) => typeof b?.description === "string" && String(b.className || "").includes("banner")
  );
  const cards = blocks
    .filter((b: any) => typeof b?.title === "string" && typeof b?.description === "string")
    .slice(0, 6);

  return {
    version: 2,
    scene: { title: lesson.title, unit: "Hoofdstuk 1" },
    meta: { mode: "explanation" as const },
    blocks: [
      {
        type: "hero" as const,
        title: content?.title ?? "Wat is Computational Thinking?",
        sub: banner?.description ?? "We leren denken in duidelijke stappen.",
      },
      ...(cards.length
        ? [
            {
              type: "grid" as const,
              columns: 3 as const,
              items: cards.map((c: any, i: number) => ({
                badge: String(i + 1),
                title: String(c.title),
                body: String(c.description),
              })),
            },
          ]
        : []),
      { type: "callout" as const, text: "Probeer: kies een taak (ontbijt maken) en schrijf de stappen op." },
    ],
  };
}

// ---- Scripted playback (v1) ----------------------------------

type SrtCue = { idx: number; startMs: number; endMs: number; text: string };

function parseTimecode(tc: string) {
  // "00:01:02,345"
  const m = tc.match(/(\d+):(\d+):(\d+)[,\.](\d+)/);
  if (!m) return 0;
  const hh = Number(m[1]), mm = Number(m[2]), ss = Number(m[3]), ms = Number(m[4]);
  return ((hh * 60 + mm) * 60 + ss) * 1000 + ms;
}

function parseSrt(srt: string): SrtCue[] {
  const blocks = (srt || "").replace(/\r/g, "").split("\n\n").map(b => b.trim()).filter(Boolean);
  const cues: SrtCue[] = [];

  for (const b of blocks) {
    const lines = b.split("\n").map(l => l.trim());
    if (lines.length < 2) continue;

    const idx = Number(lines[0]) || (cues.length + 1);
    const timing = lines[1];
    const tm = timing.match(/(.+)\s*-->\s*(.+)/);
    if (!tm) continue;

    const startMs = parseTimecode(tm[1].trim());
    const endMs = parseTimecode(tm[2].trim());
    const text = lines.slice(2).join(" ").replace(/\s+/g, " ").trim();

    if (text) cues.push({ idx, startMs, endMs, text });
  }
  return cues;
}

type PlaybackState = {
  lessonId: LessonId;
  sectionFile: string; // e.g. "1-WatIsComputationalThinking.json"
  stepIndex: number;   // which step inside that file
  cuePos: number;      // 0-based position in parsed cues array
  currentSrtIndex: number; // 1-based SRT cue index for showAtSrtIndex comparisons
};

const PLAYBACK_KEY = "tt_playback_v1";

export function getPlaybackState(): PlaybackState | null {
  try {
    const raw = localStorage.getItem(PLAYBACK_KEY);
    return raw ? (JSON.parse(raw) as PlaybackState) : null;
  } catch {
    return null;
  }
}

export function setPlaybackState(st: PlaybackState | null) {
  if (!st) localStorage.removeItem(PLAYBACK_KEY);
  else localStorage.setItem(PLAYBACK_KEY, JSON.stringify(st));

  // Tell the UI immediately (same tab) that playback changed
  window.dispatchEvent(new Event("tt-playback"));
}

// Cache for current loaded “script package”
let cachedSectionJson: any | null = null;
let cachedCues: SrtCue[] | null = null;
let cachedSrtSrc: string | null = null;

// Load the current step payload (JSON + cues)
async function ensureCTStepLoaded(lesson: LessonBlueprint, sectionFile: string, stepIndex: number) {
  const sectionPath = `${lesson.basePath}/${sectionFile}`;
  if (!cachedSectionJson || (cachedSectionJson as any).__path !== sectionPath) {
    cachedSectionJson = await fetchJson(sectionPath);
    (cachedSectionJson as any).__path = sectionPath;
  }

  const step = cachedSectionJson?.steps?.[stepIndex];
  const srtSrc = step?.content?.srtSrc;

  if (typeof srtSrc === "string" && srtSrc !== cachedSrtSrc) {
    const srtText = await (await fetch(srtSrc, { cache: "no-store" })).text();
    cachedCues = parseSrt(srtText);
    cachedSrtSrc = srtSrc;
  }

  return { step, cues: cachedCues || [] };
}

/**
 * Start playback at the first “real” content section.
 * For CT this is sectionFile "1-WatIsComputationalThinking.json", stepIndex 0.
 */
export async function startCTPlayback(lesson: LessonBlueprint) {
  const st: PlaybackState = {
    lessonId: lesson.id,
    sectionFile: "1-WatIsComputationalThinking.json",
    stepIndex: 0,
    cuePos: 0,
    currentSrtIndex: 1,
  };
  setPlaybackState(st);

  const { cues } = await ensureCTStepLoaded(lesson, st.sectionFile, st.stepIndex);
  const first = cues[0];

  return first ? first.text : "Laten we beginnen.";
}

/** Advance to next cue; returns null when finished */
export async function nextCTCue(lesson: LessonBlueprint): Promise<string | null> {
  const st = getPlaybackState();
  if (!st) return null;

  const { cues } = await ensureCTStepLoaded(lesson, st.sectionFile, st.stepIndex);
  const nextPos = st.cuePos + 1;

  if (nextPos >= cues.length) return null;

  const cue = cues[nextPos];
  setPlaybackState({
    ...st,
    cuePos: nextPos,
    currentSrtIndex: cue.idx, // use 1-based cue index for showAtSrtIndex
  });

  return cue.text;
}

/** Build DigiBoard based on stages up to currentSrtIndex */
export async function getCTBoardForPlayback(lesson: LessonBlueprint) {
  const st = getPlaybackState();
  if (!st) return null;

  const { step } = await ensureCTStepLoaded(lesson, st.sectionFile, st.stepIndex);
  const content = step?.content;
  const stages = Array.isArray(content?.stages) ? content.stages : [];

const visibleStages = stages.filter((s: any) => (s?.showAtSrtIndex ?? 1) <= st.currentSrtIndex);

// ✅ Use ONLY the latest visible stage (prevents duplicates when JSON repeats content)
const latestStage = visibleStages.length ? visibleStages[visibleStages.length - 1] : null;
const allBlocks = Array.isArray(latestStage?.blocks) ? latestStage.blocks : [];

  // Convert authored blocks → your existing DigiBoardPanel v2 types (hero/grid/callout)
  const banner = allBlocks.find(
    (b: any) => typeof b?.description === "string" && String(b.className || "").includes("banner")
  );
  const cards = allBlocks
    .filter((b: any) => typeof b?.title === "string" && typeof b?.description === "string")
    .slice(0, 6);

  return {
    version: 2,
    scene: { title: lesson.title, unit: "Hoofdstuk 1" },
    meta: { mode: "explanation" as const },
    blocks: [
      {
        type: "hero" as const,
        title: content?.title ?? "Wat is Computational Thinking?",
        sub: banner?.description ?? "We bouwen stap-voor-stap denkvaardigheden.",
      },
      ...(cards.length
        ? [
            {
              type: "grid" as const,
              columns: 3 as const,
              items: cards.map((c: any, i: number) => ({
                badge: String(i + 1),
                title: String(c.title),
                body: String(c.description),
              })),
            },
          ]
        : []),
    ],
  };
}